import React, { useState, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Download, Upload, AlertTriangle, CheckCircle2, X, Database, ArrowRight } from "lucide-react";

export default function AdminBackup() {
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [exportCounts, setExportCounts] = useState(null);
  const [importPreview, setImportPreview] = useState(null);   // {file, payload, counts}
  const [importResult, setImportResult] = useState(null);

  if (user?.role !== "admin") {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 900 }}>Admin · Backup &amp; restore</h1>
        <p style={{ color: "#b91c1c" }}>This page is only available to the admin account.</p>
      </div>
    );
  }

  const doExport = async () => {
    setBusy(true); setError(""); setSuccess(""); setExportCounts(null);
    try {
      const { data } = await api.get("/admin/factory-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newell-factory-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportCounts(data.counts);
      setSuccess(`Backup downloaded — ${data.counts.areas} areas, ${data.counts.structures} structures, ${data.counts.inspections} inspections, ${data.counts.site_maps} site map.`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onFilePick = async (file) => {
    setError(""); setSuccess(""); setImportPreview(null); setImportResult(null);
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { setError("File too large (max 50 MB)"); return; }
    const text = await file.text();
    let payload;
    try { payload = JSON.parse(text); }
    catch { setError("That doesn't look like a JSON backup file."); return; }
    if (!payload || typeof payload !== "object" || !payload.version) {
      setError("Backup file is missing the `version` field. Did you export this from a Newell backend?");
      return;
    }
    setImportPreview({
      file,
      payload,
      counts: payload.counts || { areas: (payload.areas || []).length, structures: (payload.structures || []).length, inspections: (payload.inspections || []).length, site_maps: payload.active_site_map ? 1 : 0 },
      source: payload.source_env,
      exported_at: payload.exported_at,
    });
  };

  const doImport = async () => {
    if (!importPreview) return;
    if (!window.confirm("This will WIPE all existing areas, structures, inspections and the active site map on THIS environment, then restore from the backup file. User accounts, RAMS, handovers, push subscriptions and audit log are preserved. Continue?")) return;
    setBusy(true); setError(""); setSuccess(""); setImportResult(null);
    try {
      const { data } = await api.post("/admin/factory-import?confirm=WIPE-AND-IMPORT", importPreview.payload);
      setImportResult(data);
      setSuccess("Import complete — see counts below. Refresh /site-map to view the restored data.");
      setImportPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-backup-page" style={{ padding: 24, maxWidth: 900 }}>
      <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 11, letterSpacing: "0.1em" }}>ADMIN · BACKUP &amp; RESTORE</div>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 6px" }}>Backup &amp; restore</h1>
      <p style={{ color: "#64748b", fontSize: 13, marginBottom: 18 }}>
        Snapshot or restore the factory dataset: <strong>areas / zones</strong>, <strong>structures</strong> (with pins &amp; RAG), <strong>baseline inspections</strong>, and the <strong>calibrated site map</strong>.
        Does <strong>not</strong> touch user accounts, RAMS, handovers, push subscriptions or the audit log.
      </p>

      {error ? (
        <div data-testid="backup-error" className="card" style={{ padding: 10, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={14} /><span style={{ flex: 1, fontSize: 12 }}>{error}</span>
          <button className="btn-ghost" onClick={() => setError("")}><X size={12} /></button>
        </div>
      ) : null}
      {success ? (
        <div data-testid="backup-success" className="card" style={{ padding: 10, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 size={14} /><span style={{ flex: 1, fontSize: 12 }}>{success}</span>
          <button className="btn-ghost" onClick={() => setSuccess("")}><X size={12} /></button>
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 }}>
        {/* EXPORT */}
        <section className="card" style={{ padding: 18, borderTop: "4px solid #059669" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Download size={18} color="#059669" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Export from this environment</h2>
          </div>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
            Downloads a JSON file you can save anywhere (Drive, GitHub, USB stick). Keep it safe — it&apos;s a complete factory snapshot.
          </p>
          <button data-testid="backup-export-btn" onClick={doExport} disabled={busy} className="btn-primary" style={{ width: "100%", padding: "10px 14px", fontSize: 14 }}>
            <Download size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {busy ? "Exporting…" : "Export factory data"}
          </button>
          {exportCounts ? (
            <div className="mono" style={{ marginTop: 12, padding: 10, background: "#ecfdf5", color: "#065f46", fontSize: 11, borderRadius: 3 }}>
              <div>· {exportCounts.areas} areas / zones</div>
              <div>· {exportCounts.structures} structures</div>
              <div>· {exportCounts.inspections} baseline inspections</div>
              <div>· {exportCounts.site_maps} site map (image + calibration)</div>
            </div>
          ) : null}
        </section>

        {/* IMPORT */}
        <section className="card" style={{ padding: 18, borderTop: "4px solid #dc2626" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Upload size={18} color="#dc2626" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Import into this environment</h2>
          </div>
          <p style={{ fontSize: 12, color: "#b91c1c", marginBottom: 14, fontWeight: 600 }}>
            <AlertTriangle size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
            DESTRUCTIVE — wipes existing areas/structures/inspections/site map on this environment, then restores from the file.
          </p>
          <label className="btn-ghost" style={{ display: "block", textAlign: "center", padding: "10px 14px", fontSize: 14, cursor: "pointer", border: "2px dashed #cbd5e1", marginBottom: 12 }}>
            <Upload size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Select backup JSON file…
            <input ref={fileRef} data-testid="backup-import-file" type="file" accept="application/json,.json" onChange={(e) => onFilePick(e.target.files?.[0])} style={{ display: "none" }} />
          </label>

          {importPreview ? (
            <div data-testid="backup-import-preview" className="card" style={{ padding: 12, background: "#fff7ed", borderLeft: "3px solid #f59e0b", marginBottom: 12 }}>
              <div className="mono" style={{ fontSize: 10, color: "#92400e", letterSpacing: "0.1em", marginBottom: 4 }}>READY TO IMPORT</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{importPreview.file.name}</div>
              <div className="mono" style={{ fontSize: 11, color: "#7c2d12" }}>
                <div>· {importPreview.counts.areas} areas / zones</div>
                <div>· {importPreview.counts.structures} structures</div>
                <div>· {importPreview.counts.inspections} inspections</div>
                <div>· {importPreview.counts.site_maps} site map</div>
                {importPreview.source ? <div style={{ marginTop: 4 }}>· source: {importPreview.source}</div> : null}
                {importPreview.exported_at ? <div>· exported: {new Date(importPreview.exported_at).toLocaleString("en-GB")}</div> : null}
              </div>
              <button data-testid="backup-import-confirm-btn" onClick={doImport} disabled={busy} style={{ width: "100%", marginTop: 10, padding: "10px 14px", background: "#dc2626", color: "#fff", border: 0, borderRadius: 3, fontWeight: 700, fontSize: 13, cursor: busy ? "not-allowed" : "pointer" }}>
                {busy ? "Importing…" : "⚠ WIPE & IMPORT NOW"}
              </button>
            </div>
          ) : null}

          {importResult ? (
            <div data-testid="backup-import-result" className="mono" style={{ padding: 10, background: "#ecfdf5", color: "#065f46", fontSize: 11, borderRadius: 3 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>IMPORT COMPLETE</div>
              <div>· wiped: {importResult.wiped.structures} structures, {importResult.wiped.inspections} inspections, {importResult.wiped.areas} areas, {importResult.wiped.site_maps} maps</div>
              <div>· restored: {importResult.restored.areas} areas, {importResult.restored.structures} structures, {importResult.restored.inspections} inspections, {importResult.restored.site_maps} maps</div>
              <div style={{ marginTop: 6 }}>
                <a href="/site-map" data-testid="backup-go-to-map" style={{ color: "#0f172a", fontWeight: 700, fontFamily: "inherit" }}>View on site map <ArrowRight size={11} style={{ verticalAlign: "middle" }} /></a>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {/* Cross-environment instructions */}
      <section className="card" style={{ padding: 18, marginTop: 18, background: "#f8fafc", borderTop: "4px solid #0f172a" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 800 }}><Database size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />How to copy Preview → Production</h3>
        <ol style={{ fontSize: 12, color: "#334155", paddingLeft: 20, margin: 0 }}>
          <li style={{ marginBottom: 6 }}>While logged into <strong>preview</strong> (this environment), click <strong>Export factory data</strong>. The JSON downloads to your computer.</li>
          <li style={{ marginBottom: 6 }}>Log into <a href="https://mccain.newellscaffolding.co.uk/admin/backup" target="_blank" rel="noreferrer" style={{ color: "#0f172a", fontWeight: 700 }}>production</a> as admin, navigate to this same page.</li>
          <li style={{ marginBottom: 6 }}>Click <strong>Select backup JSON file</strong>, choose the downloaded file, then click <strong>WIPE &amp; IMPORT NOW</strong>.</li>
          <li>Done. The production site map will mirror preview exactly.</li>
        </ol>
        <div className="mono" style={{ marginTop: 10, padding: 6, background: "#fff", border: "1px solid #e2e8f0", color: "#64748b", fontSize: 10 }}>
          The new endpoints &amp; this page must exist on production first — i.e. <em>redeploy production once</em> before the first import.
        </div>
      </section>
    </div>
  );
}
